import React from "react";
import { useTranslation } from "react-i18next";
import {
  CarWithContextModel,
  TireSetDetailModel,
} from "../../../infrastructure/slices/requestQuickAddSliceModel";

type RequestType = "deliver" | "pickup";

interface TiresetSelectionProps {
  isPickupDisabled: () => boolean;
  selectedCar: CarWithContextModel | null;
  selectedTireset: string | null;
  showTiresetSelect: boolean;
  requestType: RequestType | null;
  hasNoValidTiresets: boolean;
  onTiresetChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

export const TiresetSelection: React.FC<TiresetSelectionProps> = ({
  isPickupDisabled,
  selectedCar,
  selectedTireset,
  showTiresetSelect,
  requestType,
  hasNoValidTiresets,
  onTiresetChange,
}) => {
  const { t } = useTranslation("common");

  // Don't show anything if no car is selected or no request type
  if (!selectedCar || !requestType) {
    return null;
  }

  // Type guard to ensure we have proper types
  const getValidTiresetDetails = (
    car: CarWithContextModel,
  ): TireSetDetailModel[] => {
    if (!car.tireset_details || !Array.isArray(car.tireset_details)) {
      return [];
    }
    return car.tireset_details as TireSetDetailModel[];
  };

  const tiresetDetails = getValidTiresetDetails(selectedCar);

  const selectedDetail: TireSetDetailModel | undefined = tiresetDetails.find(
    (detail: TireSetDetailModel) => detail.tireset.id == selectedTireset,
  );

  // Helper function to capitalize tire type and handle all_season
  const getTireTypeLabel = (type: string): string => {
    if (type === "all_season") return t("All season");
    if (type === "summer") return t("Summer");
    if (type === "winter") return t("Winter");
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <>
      {/* Always show a section for tire set selection */}
      <div className="mt-3">
        <label className="form-label fw-bold">{t("Tire set selection")}</label>

        {((hasNoValidTiresets &&
          !(requestType === "pickup" && showTiresetSelect)) ||
          (requestType === "pickup" && isPickupDisabled())) && (
          <div className="alert alert-warning mt-2" role="alert">
            <div className="d-flex align-items-center">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <div>
                <strong>
                  {t("Tire set is not available for")}{" "}
                  {t(requestType === "deliver" ? "delivery" : "pickup")}
                </strong>
                <div className="mt-1">
                  {requestType === "deliver" && (
                    <>
                      {t(
                        "The tire set for this car is currently not in hotel; only a pickup request is possible.",
                      )}
                    </>
                  )}
                  {requestType === "pickup" && (
                    <>
                      {t(
                        "The tire set for this car is currently in hotel; only a deliver request is possible.",
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {hasNoValidTiresets &&
          requestType === "pickup" &&
          showTiresetSelect && (
            <div className="alert alert-danger mt-2" role="alert">
              <div className="d-flex align-items-center">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <div>
                  <strong>
                    {t(
                      "We already have this set in storage. Normally, one set is delivered before we pick up another.",
                    )}
                  </strong>
                  <div className="mt-1">
                    {requestType === "pickup" && (
                      <>
                        {t(
                          "Would you like to have an additional set picked up for this license plate? Then proceed.",
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* Always show the dropdown for pickup, even if hasNoValidTiresets */}
        {requestType === "pickup" && showTiresetSelect && (
          <div>
            <div className="mb-2">
              <small className="text-muted">
                {t("Please choose which one you want to add:")}
              </small>
            </div>
            <select
              value={selectedTireset || ""}
              onChange={onTiresetChange}
              className="form-select"
            >
              <option value="">{t("Select a tire set...")}</option>
              {/* If no valid tire set, show new options */}
              {hasNoValidTiresets &&
                ["summer", "winter", "all_season"].map((type) => (
                  <option key={type} value={"new:" + type}>
                    {`${getTireTypeLabel(type)} (${t("new")})`}
                  </option>
                ))}
            </select>
          </div>
        )}

        {!hasNoValidTiresets &&
          selectedTireset &&
          !showTiresetSelect &&
          !(requestType === "pickup" && isPickupDisabled()) && (
            <div className="alert alert-success mt-2" role="alert">
              <div className="d-flex align-items-center">
                <i className="fas fa-check-circle me-2"></i>
                <div>
                  <strong>{t("Tire set selected")}</strong>
                  {selectedDetail && (
                    <div className="mt-1">
                      {getTireTypeLabel(selectedDetail.tireset.type)}{" "}
                      {t("tires")}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
      </div>
    </>
  );
};
